<script setup>
import {onMounted, ref} from "vue";
import BaseModal from "@/components/global-themed/BaseModal.vue";
import FixPanelHeader from "@/components/approval-center/partials/ui/FixPanelHeader.vue";
import FixPanelHeaderTitle from "@/components/approval-center/partials/ui/FixPanelHeaderTitle.vue";
import FixPanelIcon from "@/components/approval-center/partials/ui/FixPanelIcon.vue";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import ContentFeedback from "@/components/approval-center/modals/advanced-revision/ContentFeedback.vue";
import FullFeedback from "@/components/approval-center/modals/advanced-revision/FullFeedback.vue";
import UnsavedChangesModal from "@/components/approval-center/modals/advanced-revision/UnsavedChangesModal.vue";
import useAdvancedRevisionFeedback from "@/composables/approval-center/advanced-revision/useAdvancedRevisionFeedback";
import useApprovalFlow from "@/composables/approval-center/useApprovalFlow";
import BlogTourIntro from "@/components/tours/blog/BlogTourIntro.vue";
import ToneOfVoiceIntro from "@/components/tours/tone-of-voice/ToneOfVoiceIntro.vue";
import AiBusinessProfileIntro from "@/components/tours/ai-business-profile/AiBusinessProfileIntro.vue";

defineProps({
  title: {
    type: String,
    default: 'Advanced Revision'
  },
  approvableContent: {
    type: String,
    required: true,
  },
  type: {
    type: String,
    required: true,
  }
});

const emit = defineEmits(['close', 'confirm']);
const showUnsavedChangesModal = ref(false);

const {
  hasFeedback,
  feedbackItems,
  activeFeedbackItem,
} = useAdvancedRevisionFeedback();

const {revisionErrors} = useApprovalFlow();

const handleClose = () => {
  if (!hasFeedback.value) {
    showUnsavedChangesModal.value = false;
    emit('close')
  }
  showUnsavedChangesModal.value = true;
}

onMounted(() => {
  revisionErrors.value = [];
})
</script>

<template>
  <Teleport to=".tailwind">
    <BaseModal
      modal-class="tw-w-[90vw] tw-max-w-[1312px] tw-max-h-[80vh]"
      @close="handleClose"
    >
      <div class="tw-px-14 tw-pb-14 tw-pt-7 tw-flex tw-flex-col">
        <FixPanelHeader>
          <FixPanelIcon icon-path="icons/notebook" />
          <FixPanelHeaderTitle>{{ title }}</FixPanelHeaderTitle>
        </FixPanelHeader>

        <div class="tw-mb-8">
          <ContentFeedback
            :active="feedbackItems.size > 0"
            :content="approvableContent"
            :prevent-scroll="!!activeFeedbackItem"
          />
        </div>

        <FullFeedback :type="type" />

        <div class="tw-flex tw-justify-end tw-gap-4">
          <DefaultButton
            variant="primary-inverted"
            @click="handleClose"
          >
            Exit Feedback Mode
          </DefaultButton>
          <DefaultButton
            id="advanced-revision-confirm-button"
            :disabled="!hasFeedback"
            @click="emit('confirm')"
          >
            Confirm Feedback
          </DefaultButton>
        </div>
      </div>
    </BaseModal>

    <BlogTourIntro v-if="type === 'Blog Post'" />
    <AiBusinessProfileIntro v-if="type === 'AI Business Profile'" />
    <ToneOfVoiceIntro v-if="type === 'Tone of Voice'" />
  </Teleport>

  <UnsavedChangesModal
    v-if="showUnsavedChangesModal"
    @back="showUnsavedChangesModal = false"
    @close="emit('close')"
  />
</template>
